import { JitsiState } from './jitsi';
import { Board } from './board';
import { LoggedInUser } from '.';

export interface SessionFullState extends SessionState {
  Jitsi: JitsiState;
}

export interface SessionState {
  sessionList: Session[];
  activeSession: Session | null;
  users: LoggedInUser[];
}

export interface Session {
  id: number;
  name: string;
  pin: string;
  status: SessionStatus;
  conferenceActive: boolean;
  board: Board;
  updatedAt: string;
  externalConferenceLink?: string;
}

export enum SessionStatus {
  PENDING,
  STARTED,
  PAUSED,
  STOPPED,
  DELETED,
}

/* ACTIONS */

export enum SessionActionsKeys {
  JOIN_SESSION = 'JOIN_SESSION',
  CREATE_SESSION = 'CREATE_SESSION',
  LOAD_GUEST_SESSION = 'LOAD_GUEST_SESSION',
  LOAD_SESSION = 'LOAD_SESSION',
  CHANGE_SESSION_STATUS = 'CHANGE_SESSION_STATUS',
  RESET_STATE = 'RESET_STATE',
  LIST_SESSIONS = 'LIST_SESSIONS',
  BOARD_LIST_SESSIONS = 'BOARD_LIST_SESSIONS',
  LEAVE_SESSION = 'LEAVE_SESSION',
  START_SESSION = 'START_SESSION',
  PAUSE_SESSION = 'PAUSE_SESSION',
  DELETE_SESSION = 'DELETE_SESSION',
  SOCKET_ON_STARTED_SESSION = 'SOCKET_ON_STARTED_SESSION',
  SOCKET_ON_PAUSED_SESSION = 'SOCKET_ON_PAUSED_SESSION',
  SOCKET_ON_DELETED_SESSION = 'SOCKET_ON_DELETED_SESSION',
  SOCKET_ON_JOINED_SESSION = 'SOCKET_ON_JOINED_SESSION',
  SOCKET_ON_LEFT_SESSION = 'SOCKET_ON_LEFT_SESSION',
}

export interface SessionActionsPayload {
  [SessionActionsKeys.JOIN_SESSION]: Session['pin'];
  [SessionActionsKeys.CREATE_SESSION]: { boardId: number | null; name: string };
  [SessionActionsKeys.LOAD_SESSION]: number;
  [SessionActionsKeys.LOAD_GUEST_SESSION]: string;
  [SessionActionsKeys.CHANGE_SESSION_STATUS]: Session['status'];
  [SessionActionsKeys.RESET_STATE]: void;
  [SessionActionsKeys.LIST_SESSIONS]: void;
  [SessionActionsKeys.BOARD_LIST_SESSIONS]: number;
  [SessionActionsKeys.LEAVE_SESSION]: void;
  [SessionActionsKeys.START_SESSION]: void;
  [SessionActionsKeys.PAUSE_SESSION]: void;
  [SessionActionsKeys.DELETE_SESSION]: { pin: Session['pin']; id: Session['id'] };
  [SessionActionsKeys.SOCKET_ON_STARTED_SESSION]: void;
  [SessionActionsKeys.SOCKET_ON_PAUSED_SESSION]: void;
  [SessionActionsKeys.SOCKET_ON_DELETED_SESSION]: { session_pin: Session['pin'] };
  [SessionActionsKeys.SOCKET_ON_JOINED_SESSION]: { user: LoggedInUser };
  [SessionActionsKeys.SOCKET_ON_LEFT_SESSION]: { user: LoggedInUser };
}

/* MUTATIONS */

export enum SessionMutationsKeys {
  SESSION_RESET_STATE = 'SESSION_RESET_STATE',
  JOIN_SESSION = 'JOIN_SESSION',
  CREATE_SESSION = 'CREATE_SESSION',
  CHANGE_SESSION_STATUS = 'CHANGE_SESSION_STATUS',
  SET_LIST_SESSIONS = 'SET_LIST_SESSIONS',
  ADD_SESSION = 'ADD_SESSION',
  LOAD_SESSION = 'LOAD_SESSION',
  LEAVE_SESSION = 'LEAVE_SESSION',
  REMOVE_SESSIONS_FOR_BOARD = 'REMOVE_SESSIONS_FOR_BOARD',
  DELETE_SESSION = 'DELETE_SESSION',
  ADD_USER = 'ADD_USER',
  REMOVE_USER = 'REMOVE_USER',
  RESET_USERS = 'RESET_USERS',
}

export interface SessionMutationsPayload {
  [SessionMutationsKeys.JOIN_SESSION]: Session['pin'];
  [SessionMutationsKeys.CREATE_SESSION]: void;
  [SessionMutationsKeys.CHANGE_SESSION_STATUS]: Session['status'];
  [SessionMutationsKeys.SESSION_RESET_STATE]: void;
  [SessionMutationsKeys.SET_LIST_SESSIONS]: Session[];
  [SessionMutationsKeys.ADD_SESSION]: Session;
  [SessionMutationsKeys.LOAD_SESSION]: Session;
  [SessionMutationsKeys.LEAVE_SESSION]: void;
  [SessionMutationsKeys.REMOVE_SESSIONS_FOR_BOARD]: number;
  [SessionMutationsKeys.DELETE_SESSION]: { pin: Session['pin']; id: Session['id'] };
  [SessionMutationsKeys.ADD_USER]: LoggedInUser;
  [SessionMutationsKeys.REMOVE_USER]: LoggedInUser;
  [SessionMutationsKeys.RESET_USERS]: void;
}

/* GETTERS */

export enum SessionGettersKeys {
  sessionPin = 'sessionPin',
  sessionStatus = 'sessionStatus',
  sessionJoined = 'sessionJoined',
  sessionList = 'sessionList',
  sessionUsers = 'sessionUsers',
  sessionExternalLink = 'sessionExternalLink',
}

export interface SessionGettersReturns {
  [SessionGettersKeys.sessionPin]: Session['pin'] | null;
  [SessionGettersKeys.sessionStatus]: Session['status'] | null;
  [SessionGettersKeys.sessionJoined]: boolean;
  [SessionGettersKeys.sessionExternalLink]: string | undefined;
  [SessionGettersKeys.sessionList]: SessionState['sessionList'];
  [SessionGettersKeys.sessionUsers]: SessionState['users'];
}
